<template>
    <div v-if="!displayModalChoixSite" class="container">
        <div class="title-container">
            <h1>
                {{ $t('intervention.request.title') }}
                <ReglePastille cle="demandeIntervention.definition" />
            </h1>
        </div>
        <div class="content-container">
            <div class="filter-container">
                <div v-if="!isUniqueSite" class="filter-site-container">
                    <h2 id="site-filter-label">
                        {{ $t('intervention.non-preventive.filter') }}
                        <ReglePastille cle="suiviDemandeIntervention.filtreSiteUnique" />
                    </h2>
                    <ButtonBlue
                        :disabled="tableData.length === 0 && isLoading"
                        :on-click="selectionnerSite"
                        :text="$t('bouton.choix.site')"
                        aria-labelledby="site-filter-label"
                        class="button-confirm btn"
                    />
                </div>
                <Filter
                    :filter="filter"
                    :statusList="statusList"
                    @addFilterStatus="addFilterStatus"
                    @clearFilter="clearFilter"
                    @removeFilterStatus="removeFilterStatus"
                    @search="search"
                    @setCreatedDateFrom="this.setCreatedDateFrom"
                    @setCreatedDateTo="this.setCreatedDateTo"
                    @setDemandeInterventionId="setDemandeInterventionId"
                    @statusToggleSelectAll="statusToggleSelectAll"
                />
            </div>
            <div class="dataTable">
                <div class="headerWrapper">
                    <h2>
                        {{ count }}
                        {{ $tc('interventionRequest.dataTable.title', count > 1 ? 2 : 1) }}
                        <ReglePastille cle="suiviDemandeIntervention.portee" />
                        <ReglePastille cle="suiviDemandeIntervention.selectionTout" />
                    </h2>
                    <BorderedIconButton
                        v-if="!isExportDisabled"
                        :imgUrl="iconeXls"
                        :title="$t('list.export')"
                        @click="exportXls"
                    />
                    <ReglePastille cle="suiviDemandeIntervention.exporterActif" />
                </div>
                <LoaderComponent v-if="tableData.length === 0 && isLoading" class="loader" />
                <div v-else ref="content" class="datatable-container" @scroll="handleScroll">
                    <DataTable
                        :count="+count"
                        :isLoading="isLoading"
                        :stop-propa-on-pdf="true"
                        :tableData="tableData"
                        :tableHeader="tableHeader"
                        @downloadPdf="downloadPdf"
                        @select="goToPage"
                        @sort="doSort"
                    />
                </div>
            </div>
            <WaitingModal v-if="isExportXlsLoading" />
        </div>
    </div>
    <ModalChoixSite
        v-else
        :is-multi-site="true"
        :inclure-termines="true"
        :selected-site="null"
        :selected-sites="filter.siteList"
        @cancel="modalChoixSiteCancel"
        @valider="validerSite"
    />
</template>

<script lang="ts">
import DataTable from '@/components/ui-components/DataTable.vue';
import { statusName } from '@/components/ui-components/StatusTag.vue';
import { Site } from '@/models/Site';
import { SortType, sortValue } from '@/models/SortType';
import { TableHeader } from '@/models/TableHeader';
import Filter from '@/modules/Common/components/Filter.vue';
import SiteListService, { SiteSearchDTO } from '@/modules/SiteList/service/SiteListService';
import { defineComponent, onMounted, ref } from 'vue';
import { DemandeInterventionActions } from '../store/action-types';
import { DEMANDE_INTERVENTION } from '../store/mutation-types';
import LoaderComponent from '@/components/ui-components/LoaderComponent.vue';
import dayjs from 'dayjs';
import router from '@/router';
import { store } from '@/plugins/store';
import BorderedIconButton from '@/components/ui-components/BorderedIconButton.vue';
import WaitingModal from '@/components/ui-components/WaitingModal.vue';
import DemandeInterventionService from '@/modules/DemandeIntervention/services/DemandeInterventionService';
import ModalChoixSite from '@/modules/Common/components/ModalChoixSite.vue';
import ButtonBlue from '@/components/ui-components/ButtonBlue.vue';
import { EtatDemandeIntervention } from '@/models/DemandeIntervention';
import { InterventionRequest } from '@/models/InterventionRequest';
import ReglePastille from '@/components/regles/ReglePastille.vue';

interface Data {
    siteList: any[];
    statusList: string[];
    filter: {
        siteList: any[];
        statusList: string[];
        demandeInterventionId: string;
        createdDateFrom: string;
        createdDateTo: string;
    };
    siteListTotal: any[];
    debounce: number;
    searchSite: string;
    displayModalChoixSite: boolean;
    isUniqueSite: boolean;
}

export default defineComponent({
    name: 'ListInterventionPage',
    components: { Filter, DataTable, LoaderComponent, BorderedIconButton, WaitingModal, ButtonBlue, ModalChoixSite, ReglePastille },
    data(): Data {
        return {
            siteList: [],
            statusList: [],
            filter: {
                siteList: [],
                statusList: [],
                demandeInterventionId: '',
                createdDateFrom: '',
                createdDateTo: '',
            },
            siteListTotal: [],
            debounce: 0,
            searchSite: '',
            displayModalChoixSite: false,
            isUniqueSite: false,
        };
    },
    setup() {
        const content = ref<Element>();

        onMounted(() => {
            content.value;
        });

        return { content };
    },
    beforeMount() {
        this.filter.siteList = this.filterSite;
        SiteListService.getSiteList(new SiteSearchDTO(0, '', true, true)).then(res => {
            if (res.data.length === 1) {
                this.isUniqueSite = true;
                this.displayModalChoixSite = false;
            }
            this.siteList = res.data.map(
                ({ id, chantierId, description }: { id: string; chantierId: number; description: string }) => ({
                    id,
                    chantierId,
                    description,
                })
            );
            this.siteListTotal = this.siteList;
        });
        this.statusList = Object.values(EtatDemandeIntervention);
        this.filter.demandeInterventionId = this.filterDemandeInterventionId;
        this.filter.createdDateFrom = this.filterCreatedDateFrom;
        this.filter.createdDateTo = this.filterCreatedDateTo;
        if (!this.displayModalChoixSite) {
            this.getDemandesInterventionData();
        }
    },
    mounted() {
        this.content?.scrollTo({ top: this.scroll });
        this.filter.statusList = this.filterStatus;
    },
    methods: {
        getDemandesInterventionData(page = 0) {
            page === 0 && this.content?.scrollTo({ top: 0 });
            this.$store.dispatch(
                `suiviDemandeInterventionModuleState/${DemandeInterventionActions.GET_DEMANDES_INTERVENTION}`,
                {
                    page,
                    sort: this.sort && sortValue(this.sort.sortData, this.sort.sortStatus),
                    etats: this.filterStatus?.length === this.statusList?.length ? [] : this.filterStatus,
                    chantierIds:
                        this.filterSite?.length === this.siteListTotal?.length
                            ? []
                            : this.filterSite?.map(x => x.chantierId),
                    adresseProjetIds:
                        this.filterSite?.length === this.siteListTotal?.length ? [] : this.filterSite?.map(x => x.id),
                    idDemande: this.filterDemandeInterventionId !== '' ? this.filterDemandeInterventionId : undefined,
                    mesDemandes: this.filterMine,
                    dateDebut: this.filterCreatedDateFrom !== '' ? this.filterCreatedDateFrom : undefined,
                    dateFin: this.filterCreatedDateTo !== '' ? this.filterCreatedDateTo : undefined,
                }
            );
        },
        exportXls() {
            store.dispatch(`suiviDemandeInterventionModuleState/${DemandeInterventionActions.EXPORT_XLS}`, {
                sort: this.sort && sortValue(this.sort.sortData, this.sort.sortStatus),
                etats: this.filterStatus?.length === this.statusList?.length ? [] : this.filterStatus,
                chantierIds:
                    this.filterSite?.length === this.siteListTotal?.length
                        ? []
                        : this.filterSite?.map(x => x.chantierId),
                idDemande: this.filterDemandeInterventionId !== '' ? this.filterDemandeInterventionId : undefined,
                mesDemandes: this.filterMine,
                dateDebut: this.filterCreatedDateFrom !== '' ? this.filterCreatedDateFrom : undefined,
                dateFin: this.filterCreatedDateTo !== '' ? this.filterCreatedDateTo : undefined,
            });
        },
        search() {
            this.$store.commit(`suiviDemandeInterventionModuleState/${DEMANDE_INTERVENTION.RESET_ITEMS}`);
            this.doFilter();
        },
        doFilter() {
            this.$store.commit(
                `suiviDemandeInterventionModuleState/${DEMANDE_INTERVENTION.SET_FILTER_SITE}`,
                this.filter.siteList
            );
            this.$store.commit(
                `suiviDemandeInterventionModuleState/${DEMANDE_INTERVENTION.SET_FILTER_STATUS}`,
                this.filter.statusList
            );
            this.$store.commit(
                `suiviDemandeInterventionModuleState/${DEMANDE_INTERVENTION.SET_FILTER_DEMANDE_INTERVENTION_ID}`,
                this.filter.demandeInterventionId
            );
            this.$store.commit(
                `suiviDemandeInterventionModuleState/${DEMANDE_INTERVENTION.SET_FILTER_CREATED_DATE_FROM}`,
                this.filter.createdDateFrom
            );
            this.$store.commit(
                `suiviDemandeInterventionModuleState/${DEMANDE_INTERVENTION.SET_FILTER_CREATED_DATE_TO}`,
                this.filter.createdDateTo
            );
            this.getDemandesInterventionData();
        },
        doSort(sort: { sortData: string; sortStatus: SortType }) {
            const { sortData, sortStatus } = sort;
            this.$store.commit(`suiviDemandeInterventionModuleState/${DEMANDE_INTERVENTION.SET_SORT}`, {
                sortData,
                sortStatus,
            });
            this.$store.commit(`suiviDemandeInterventionModuleState/${DEMANDE_INTERVENTION.RESET_ITEMS}`);
            this.getDemandesInterventionData();
        },
        setDemandeInterventionId(value: string): void {
            this.filter.demandeInterventionId = value;
        },
        setCreatedDateFrom(value: string): void {
            this.filter.createdDateFrom = value;
        },
        setCreatedDateTo(value: string): void {
            this.filter.createdDateTo = value;
        },
        addFilterStatus(status: string) {
            this.filter.statusList = [...this.filter.statusList, status];
        },
        removeFilterStatus(status: string) {
            this.filter.statusList = this.filter.statusList.filter(statusData => statusData !== status);
        },
        clearFilter() {
            this.multipleSelectSearchSite('');
            this.filter.siteList = this.siteListTotal ?? [];
            this.filter.statusList = this.statusList ?? [];
            this.filter.demandeInterventionId = '';
            this.filter.createdDateFrom = '';
            this.filter.createdDateTo = '';

            this.doFilter();
        },
        handleScroll() {
            const top = this.content?.scrollTop ?? 0;
            const max = this.content?.scrollHeight ?? 0;
            const height = this.content?.clientHeight ?? 0;
            this.$store.commit(`suiviDemandeInterventionModuleState/${DEMANDE_INTERVENTION.SET_SCROLL}`, top);
            if (max - top <= height * 1.5 && !this.isLoading && this.page + 1 < this.numberOfPages) {
                this.getDemandesInterventionData(this.page + 1);
            }
        },
        statusToggleSelectAll() {
            this.filter.statusList = this.filter.statusList.length === this.statusList.length ? [] : this.statusList;
        },
        multipleSelectSearchSite(search: string) {
            this.searchSite = search;
            clearTimeout(this.debounce);
            setTimeout(() => {
                SiteListService.getSiteList(new SiteSearchDTO(0, search, true, true)).then(res => {
                    this.siteList = res.data.map(
                        ({ id, chantierId, description }: { id: string; chantierId: number; description: string }) => ({
                            id,
                            chantierId,
                            description,
                        })
                    );
                });
            }, 600);
        },
        goToPage(item: { id: { pageName: string; value: number } }) {
            router.push({ name: item.id.pageName, params: { id: item.id.value } });
        },
        async downloadPdf(item: { id: { pageName: string; value: number } }) {
            const listeDemandemandesInterventions: InterventionRequest[] =
                this.$store.getters['suiviDemandeInterventionModuleState/getDemandesInterventionsItems'];
            for (const demande of listeDemandemandesInterventions) {
                if (demande.id === item.id.value) {
                    const response = await DemandeInterventionService.getRapportsPDF(demande.id);
                    if (response.status === 200 && response.data !== undefined) {
                        const file = new Blob([response.data], { type: 'application/zip' });
                        const link = document.createElement('a');
                        link.href = URL.createObjectURL(file);
                        link.target = 'blank';
                        link.download = 'rapports_interventions';
                        link.click();
                        URL.revokeObjectURL(link.href);
                    }
                }
            }
        },
        validerSite(sites: Site[]): void {
            this.siteList = sites;
            this.displayModalChoixSite = false;
            store.commit(`suiviDemandeInterventionModuleState/${DEMANDE_INTERVENTION.SET_FILTER_SITE}`, sites);
            store.commit(`suiviDemandeInterventionModuleState/${DEMANDE_INTERVENTION.RESET_ITEMS}`);
            this.getDemandesInterventionData();
        },
        modalChoixSiteCancel(): void {
            this.siteList = this.siteListTotal;
            this.displayModalChoixSite = false;
            if (this.tableData.length === 0 && !this.isLoading) this.getDemandesInterventionData();
        },
        selectionnerSite(): void {
            this.displayModalChoixSite = true;
        },
    },
    computed: {
        tableData() {
            return this.$store.getters['suiviDemandeInterventionModuleState/getDemandesInterventionsItems'].map(
                ({
                    id,
                    libelle,
                    adresseSiteLibelle,
                    delai,
                    statut,
                    phases,
                    dateAppelClient,
                    rapportPresent,
                }: InterventionRequest) => {
                    statut = statusName(statut);

                    return {
                        id: { pageName: 'interventionRequestDetailView', value: id, label: id },
                        objet: libelle,
                        site: adresseSiteLibelle,
                        equipment: {
                            libelle: phases?.[0]?.libelleEquipement ?? this.$t('noEquipment'),
                            phase: phases?.[0]?.phaseLibelle && phases[0].phaseLibelle.trim().toLowerCase() !== 'maintenance' ? phases[0].phaseLibelle : undefined,
                        },
                        date: dayjs(dateAppelClient).format('DD/MM/YYYY'),
                        delai,
                        statut,
                        pdfExist: rapportPresent,
                    };
                }
            );
        },
        tableHeader(): TableHeader[] {
            return this.$store.getters['suiviDemandeInterventionModuleState/getDemandesInterventionsTableHeader'];
        },
        sort(): {
            sortData: string;
            sortStatus: SortType;
        } {
            return this.$store.getters['suiviDemandeInterventionModuleState/getDemandesInterventionsSort'];
        },
        page() {
            return this.$store.getters['suiviDemandeInterventionModuleState/getDemandesInterventionsPage'];
        },
        count() {
            return this.$store.getters['suiviDemandeInterventionModuleState/getDemandesInterventionsCount'];
        },
        numberOfPages() {
            return this.$store.getters['suiviDemandeInterventionModuleState/getDemandesInterventionsNbOfPages'];
        },
        scroll() {
            return this.$store.getters['suiviDemandeInterventionModuleState/getDemandesInterventionsScroll'];
        },
        isLoading() {
            return this.$store.getters['suiviDemandeInterventionModuleState/getDemandesInterventionsIsLoading'];
        },
        filterSite(): any[] {
            return this.$store.getters['suiviDemandeInterventionModuleState/getDemandeInterventionFilterSite'];
        },
        filterStatus(): string[] {
            return this.$store.getters['suiviDemandeInterventionModuleState/getDemandeInterventionFilterStatus'];
        },
        filterDemandeInterventionId() {
            return this.$store.getters[
                'suiviDemandeInterventionModuleState/getDemandeInterventionFilterDemandeInterventionId'
            ];
        },
        filterCreatedDateFrom() {
            return this.$store.getters[
                'suiviDemandeInterventionModuleState/getDemandeInterventionFilterCreatedDateFrom'
            ];
        },
        filterCreatedDateTo() {
            return this.$store.getters['suiviDemandeInterventionModuleState/getDemandeInterventionFilterCreatedDateTo'];
        },
        filterMine(): boolean {
            return this.$store.getters['suiviDemandeInterventionModuleState/getDemandeInterventionFilterMine'];
        },
        getInterventionRequestData() {
            return this.$store.getters.getInterventionRequest;
        },
        iconeXls() {
            return require('@/assets/img/xls.svg');
        },
        isExportDisabled(): boolean {
            return (
                this.filter.siteList?.length === 0 ||
                this.filter.statusList?.length === 0 ||
                this.tableData.length === 0
            );
        },
        isExportXlsLoading(): boolean {
            return store.getters['suiviDemandeInterventionModuleState/getDemandesInterventionsExportXlsIsLoading'];
        },
    },
    watch: {
        tableData() {
            this.filter.statusList = this.filterStatus.length > 0 ? this.filterStatus : this.statusList;
            this.filter.siteList = this.filterSite.length > 0 ? this.filterSite : this.siteList;
        },
        filterStatus() {
            this.content?.scrollTo({ top: 0 });
        },
        filterSite() {
            this.content?.scrollTo({ top: 0 });
        },
    },
});
</script>

<style lang="scss" scoped>
@import '@/theme/pages-recherche.scss';

@media screen and (max-width: 1631px) {
    .datatable-container {
        max-height: calc(100vh - 340px);
    }
}

@media screen and (max-width: 1531px) {
    .datatable-container {
        max-height: calc(100vh - 460px);
    }
}

@media screen and (max-width: 970px) {
    .datatable-container {
        max-height: calc(100vh - 518px);
    }
}

:deep(.containerBtn) {
    height: 21px;
}
:deep(.icon) {
    height: 20px;
}
.filter-container {
    display: flex;
    padding: 8px 16px;
    background: white;
    border-radius: 6px;
    margin-bottom: 8px;
}
.filter-site-container {
    flex-direction: column;
    width: 15%;
}
.filter {
    width: 100%;
}
.button-confirm {
    right: 16px;
}
.btn {
    width: 100%;
    border-radius: 5px;
    cursor: pointer;
    height: 40px;
    margin-top: 20px;
}
:deep(.filter) {
    margin-bottom: 0;
    padding: 0 16px;
}
</style>
